import { getPresentPlacementExtents, getPresentSafeArea } from "./clampWidgetPosition";

/** Keep in sync with AUTOMATION_SEQUENCE_LAYOUT_CANVAS in automationSequenceModel.js */
export const DESIGN_CANVAS = { width: 1280, height: 720 };

/** Matches the Present sidebar rail drawn in automation layout previews. */
export const DESIGN_SIDEBAR_WIDTH = 65;
export const DESIGN_SAFE_BUFFER = 10;
export const DESIGN_SAFE_RIGHT_BUFFER = 18;
export const DESIGN_BOTTOM_NAV_HEIGHT = 57;

const clamp = (value, minimum, maximum) => Math.max(minimum, Math.min(maximum, value));

export const getDesignSafeArea = () => ({
  left: DESIGN_SIDEBAR_WIDTH + DESIGN_SAFE_BUFFER,
  top: DESIGN_SAFE_BUFFER,
  width: Math.max(
    1,
    DESIGN_CANVAS.width - (DESIGN_SIDEBAR_WIDTH + DESIGN_SAFE_BUFFER) - DESIGN_SAFE_RIGHT_BUFFER
  ),
  height: Math.max(
    1,
    DESIGN_CANVAS.height - DESIGN_BOTTOM_NAV_HEIGHT - DESIGN_SAFE_BUFFER
  ),
});

/**
 * Present safe area when DOM is unavailable (builder modal, tests).
 * Uses the design canvas proportions as the reference viewport.
 */
export const getReferencePresentSafeArea = () => getDesignSafeArea();

export const hasNormalizedLayout = (layout = {}) =>
  Number.isFinite(Number(layout.xNorm))
  && Number.isFinite(Number(layout.yNorm))
  && Number.isFinite(Number(layout.widthNorm))
  && Number.isFinite(Number(layout.heightNorm));

const getPlacementSpan = (safeArea = {}, width = 0, height = 0) => ({
  spanX: Math.max(1, Number(safeArea.width) - Math.max(0, width)),
  spanY: Math.max(1, Number(safeArea.height) - Math.max(0, height)),
});

export const canvasRectToNormalized = (rect = {}, safeArea = getDesignSafeArea()) => {
  const width = Math.max(0, Math.round(Number(rect.width) || 0));
  const height = Math.max(0, Math.round(Number(rect.height) || 0));
  const { spanX, spanY } = getPlacementSpan(safeArea, width, height);

  return {
    xNorm: clamp((Number(rect.x) - safeArea.left) / spanX, 0, 1),
    yNorm: clamp((Number(rect.y) - safeArea.top) / spanY, 0, 1),
    widthNorm: clamp(width / Math.max(1, safeArea.width), 0, 1),
    heightNorm: clamp(height / Math.max(1, safeArea.height), 0, 1),
    zIndex: Number.isFinite(Number(rect.zIndex)) ? Number(rect.zIndex) : 1,
  };
};

export const normalizedToCanvasRect = (
  norm = {},
  safeArea = getDesignSafeArea(),
  { minWidth = 1, minHeight = 1, maxWidth, maxHeight } = {}
) => {
  const areaMaxWidth = maxWidth ?? safeArea.width;
  const areaMaxHeight = maxHeight ?? safeArea.height;
  const width = clamp(
    Math.round((Number(norm.widthNorm) || 0) * safeArea.width),
    minWidth,
    areaMaxWidth
  );
  const height = clamp(
    Math.round((Number(norm.heightNorm) || 0) * safeArea.height),
    minHeight,
    areaMaxHeight
  );
  const { spanX, spanY } = getPlacementSpan(safeArea, width, height);

  return {
    x: Math.round(safeArea.left + (Number(norm.xNorm) || 0) * spanX),
    y: Math.round(safeArea.top + (Number(norm.yNorm) || 0) * spanY),
    width,
    height,
    zIndex: Number.isFinite(Number(norm.zIndex)) ? Number(norm.zIndex) : 1,
  };
};

export const normalizedToRuntimePixels = (
  norm = {},
  safeArea = getPresentSafeArea() || getReferencePresentSafeArea(),
  { minWidth = 1, minHeight = 1 } = {}
) => {
  const width = clamp(
    Math.round((Number(norm.widthNorm) || 0) * safeArea.width),
    minWidth,
    safeArea.width
  );
  const height = clamp(
    Math.round((Number(norm.heightNorm) || 0) * safeArea.height),
    minHeight,
    safeArea.height
  );
  const { spanX, spanY } = getPlacementSpan(safeArea, width, height);

  const position = {
    x: Math.round(safeArea.left + (Number(norm.xNorm) || 0) * spanX),
    y: Math.round(safeArea.top + (Number(norm.yNorm) || 0) * spanY),
  };

  const size = { width, height };
  const extents = getPresentPlacementExtents(width, height);
  const clampedPosition = extents
    ? {
      x: clamp(position.x, extents.minX, extents.maxX),
      y: clamp(position.y, extents.minY, extents.maxY),
    }
    : position;

  return {
    position: clampedPosition,
    size,
  };
};

export const runtimePixelsToNormalized = (
  position = {},
  size = {},
  safeArea = getPresentSafeArea() || getReferencePresentSafeArea()
) =>
  canvasRectToNormalized(
    {
      x: position.x,
      y: position.y,
      width: size.width,
      height: size.height,
    },
    safeArea
  );

export const mergeLayoutWithNormalized = (canvasRect = {}, normPatch = {}) => ({
  ...canvasRect,
  ...normPatch,
});

/**
 * Resolve editor canvas layout from stored step config (normalized preferred).
 */
export const resolveCanvasLayoutFromConfig = (
  layout = {},
  { minWidth = 1, minHeight = 1, maxWidth, maxHeight } = {}
) => {
  if (hasNormalizedLayout(layout)) {
    return normalizedToCanvasRect(layout, getDesignSafeArea(), {
      minWidth,
      minHeight,
      maxWidth,
      maxHeight,
    });
  }

  return {
    x: Math.round(Number(layout.x) || 0),
    y: Math.round(Number(layout.y) || 0),
    width: Math.round(Number(layout.width) || minWidth),
    height: Math.round(Number(layout.height) || minHeight),
    zIndex: Number.isFinite(Number(layout.zIndex)) ? Number(layout.zIndex) : 1,
  };
};

/**
 * Persistable layout: canvas rect + normalized fractions.
 */
export const buildStoredVisualLayout = (
  canvasRect = {},
  { minWidth = 1, minHeight = 1, maxWidth, maxHeight } = {}
) => {
  const designSafeArea = getDesignSafeArea();
  const clampedCanvas = normalizedToCanvasRect(
    canvasRectToNormalized(canvasRect, designSafeArea),
    designSafeArea,
    { minWidth, minHeight, maxWidth, maxHeight }
  );
  const normalized = canvasRectToNormalized(clampedCanvas, designSafeArea);

  return mergeLayoutWithNormalized(clampedCanvas, normalized);
};

/**
 * Runtime position/size for countdown, reminder, birthday overlays.
 */
export const resolveRuntimeLayoutFromConfig = (
  layout = {},
  { minWidth = 1, minHeight = 1 } = {}
) => {
  const presentSafeArea = getPresentSafeArea() || getReferencePresentSafeArea();
  const normalized = hasNormalizedLayout(layout)
    ? {
      xNorm: layout.xNorm,
      yNorm: layout.yNorm,
      widthNorm: layout.widthNorm,
      heightNorm: layout.heightNorm,
      zIndex: layout.zIndex,
    }
    : canvasRectToNormalized(
      resolveCanvasLayoutFromConfig(layout, { minWidth, minHeight }),
      getDesignSafeArea()
    );

  return normalizedToRuntimePixels(normalized, presentSafeArea, { minWidth, minHeight });
};

export const applyRuntimeCascadeOffset = (
  runtimeLayout = {},
  cascadeIndex = 0,
  { offset = 40 } = {}
) => {
  const presentSafeArea = getPresentSafeArea() || getReferencePresentSafeArea();
  const position = runtimeLayout.position || {};
  const size = runtimeLayout.size || {};
  const offsetPx = Math.max(0, Number(cascadeIndex) || 0) * offset;
  const maxX = presentSafeArea.left + Math.max(0, presentSafeArea.width - (Number(size.width) || 0));
  const maxY = presentSafeArea.top + Math.max(0, presentSafeArea.height - (Number(size.height) || 0));

  return {
    ...runtimeLayout,
    position: {
      x: clamp((Number(position.x) || 0) + offsetPx, presentSafeArea.left, maxX),
      y: clamp((Number(position.y) || 0) + offsetPx, presentSafeArea.top, maxY),
    },
    size,
  };
};

export const formatLayoutPercentLabel = (layout = {}) => {
  if (!hasNormalizedLayout(layout)) {
    return "";
  }

  const widthPct = Math.round((Number(layout.widthNorm) || 0) * 100);
  const heightPct = Math.round((Number(layout.heightNorm) || 0) * 100);
  return `${widthPct}% × ${heightPct}% of screen`;
};

export const formatRuntimeLayoutDimensionLabel = (layout = {}) => {
  const { size } = resolveRuntimeLayoutFromConfig(layout);
  return `${size.width} × ${size.height}`;
};
